



























































import Vue from 'vue';
import { mixin as clickaway } from 'vue-clickaway';
import { Component } from 'vue-property-decorator';
import clamp from 'lodash/clamp';
import { DateTime } from 'luxon';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import {
    CONTRACTS,
    EventsServiceContract,
    ProcessingStartedEvent,
    ContextRelatedEvent,
    Events,
    WindowsServiceContract,
    FetchServiceContract,
} from '@/core/contracts';
import { Context, ContextUser } from '@/dto';
import store, { ContextsState, UserState } from '@/store';
import { showUploader } from '@/core/components/uploader';
import { localize } from '@/core/filters';
import ConnectionStatusInfo from './ConnectionStatusInfo.vue';

interface ContextProcessingInfo {
    timeout: NodeJS.Timeout;
    started: number;
    avg: number;
    canceled?: boolean;
}

@Component({ mixins: [clickaway], components: { ConnectionStatusInfo } })
export default class Contexts extends Vue {
    public expanded: boolean = false;
    public progress: number = 0;
    public showUploader = showUploader;

    @Inject(CONTRACTS.EventsService)
    private eventsService?: EventsServiceContract;
    @Inject(CONTRACTS.WindowsService)
    private windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.FetchService) private fetchService?: FetchServiceContract;

    private contextProcessingInfo?: ContextProcessingInfo;

    public get contextsList() {
        return ContextsState.list;
    }

    public get currentContext() {
        return ContextsState.current;
    }

    public get userDisplayName() {
        return UserState.userName;
    }

    public get currentContextName() {
        return this.currentContext ? this.currentContext.name : '...';
    }

    public get nonActiveContexts(): Context[] {
        const { currentContext } = this;

        const result: Context[] = currentContext
            ? this.contextsList.filter(item => item.id !== currentContext.id)
            : this.contextsList;

        return this.sortContexts(result);
    }

    public async mounted() {
        ContextsState.fetchContexts();

        this.wsEventsListeners.forEach(event => {
            this.eventsService!.on(event.eventName, event.handler);
        });
    }

    public toggleDevmode() {
        if (UserState.isDev) {
            store.state.devmode = !store.state.devmode;
        }
    }

    public changeContext(context: Context) {
        this.windowsService!.closeAll();
        ContextsState.changeContext(context);
        this.close();
    }

    public createNewContext() {
        this.windowsService!.closeAll();
        const name = DateTime.utc().toFormat('yyyyMMddHHmmss');
        ContextsState.createContext(name);
        this.close();
    }

    public toggle() {
        this.expanded = !this.expanded;
    }

    public open() {
        this.expanded = true;
    }

    public close() {
        this.expanded = false;
    }

    public openUserSettingsPage(): void {
        this.windowsService!.open('user-settings-page');
    }

    public openAppSettingsPage(): void {
        this.windowsService!.open('context-settings-page');
    }

    public deleteContextHandler(context: Context): void {
        let operation: Promise<any>;

        if (!context.own) {
            operation = this.leaveContextRequest(context);
        } else {
            operation = this.getAdminCount(context).then(admins => {
                if (admins > 1) {
                    return this.leaveOrDeleteContextRequest(context);
                } else {
                    return this.deleteContextRequest(context);
                }
            });
        }

        operation.then(() => ContextsState.fetchContexts());
    }

    private sortContexts(contexts: Context[]): Context[] {
        return contexts.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
    }

    private getAdminCount(context: Context): Promise<number> {
        return this.fetchService!.get<ContextUser[]>(
            `/contexts/${context.id}/users`
        ).then(users => {
            return users.reduce(
                (acc: number, current: ContextUser) =>
                    (acc += this.isAdministrator(current) ? 1 : 0),
                0
            );
        });
    }

    private isAdministrator(user: ContextUser): boolean {
        const hasRole = user.role && user.role.id;
        return !hasRole || user.role!.isAdministrator === true;
    }

    private leaveContextRequest(context: Context): Promise<any> {
        return this.windowsService!.showDialog(
            localize('ContextsLeaveContextMessage'),
            [
                { id: 1, caption: localize('ContextsYes') },
                { id: 2, caption: localize('ContextsCancel') },
            ]
        ).then(id => {
            if (id === 1) {
                return this.leaveContext(context);
            }
        });
    }

    private leaveOrDeleteContextRequest(context: Context): Promise<any> {
        return this.windowsService!.showDialog(
            localize('ContextsLeaveOrDeleteMessage'),
            [
                { id: 1, caption: localize('ContextsLeave') },
                { id: 2, caption: localize('ContextsDelete') },
                { id: 3, caption: localize('ContextsCancel') },
            ]
        ).then(id => {
            if (id === 1) {
                return this.leaveContext(context);
            } else if (id === 2) {
                return this.deleteContext(context);
            }
        });
    }

    private deleteContextRequest(context: Context): Promise<any> {
        return this.windowsService!.showDialog(
            localize('ContextsDeleteMessage'),
            [
                { id: 1, caption: localize('ContextsYes') },
                { id: 2, caption: localize('ContextsCancel') },
            ]
        ).then(id => {
            if (id === 1) {
                return this.deleteContext(context);
            }
        });
    }

    private deleteContext(context: Context): Promise<any> {
        return this.fetchService!.delete(`/contexts/${context.id}`);
    }

    private leaveContext(context: Context): Promise<any> {
        const userId = UserState.current!.id;
        return this.fetchService!.delete(
            `/contexts/${context.id}/users/${userId}/delete`
        );
    }

    private get wsEventsListeners() {
        const startProcessingHandler = (data: any) =>
            this.startProcessing(data as ProcessingStartedEvent);
        const stopProcessingHandler = (data: any) =>
            this.stopProcessing(data as ContextRelatedEvent);

        return [
            {
                eventName: Events.ProcessingStarted,
                handler: startProcessingHandler,
            },
            {
                eventName: Events.ProcessingFinished,
                handler: stopProcessingHandler,
            },
            {
                eventName: Events.ProcessingTerminated,
                handler: stopProcessingHandler,
            },
            {
                eventName: Events.ProcessingAborted,
                handler: stopProcessingHandler,
            },
        ];
    }

    private async beforeDestroy() {
        this.wsEventsListeners.forEach(event => {
            this.eventsService!.off(event.eventName, event.handler);
        });
    }

    private startProcessing(data: ProcessingStartedEvent) {
        if (
            this.currentContext &&
            Number(this.currentContext.id) === Number(data.contextId)
        ) {
            this.clearProcessing();
            this.contextProcessingInfo = {
                started: +new Date(),
                avg: data.averageProcessingTime,
                timeout: setTimeout(() => this.updateProgress(), 250),
                canceled: false,
            };
        }
    }

    private updateProgress() {
        if (
            this.contextProcessingInfo &&
            !this.contextProcessingInfo.canceled
        ) {
            const current = +new Date();
            const diff = current - this.contextProcessingInfo.started;
            this.progress = clamp(diff / this.contextProcessingInfo.avg, 0, 1);
            setTimeout(() => this.updateProgress(), 250);
        }
    }

    private clearProcessing() {
        if (this.contextProcessingInfo) {
            this.contextProcessingInfo.canceled = true;
            clearTimeout(this.contextProcessingInfo.timeout);
        }
        this.progress = 0;
    }

    private stopProcessing(data: ContextRelatedEvent) {
        if (
            this.currentContext &&
            Number(this.currentContext.id) === Number(data.contextId)
        ) {
            this.clearProcessing();
        }
        const terminated: boolean = data.type === 'processingTerminatedEvent';
        const ContextID: number = data.contextId;
        ContextsState.updateTerminated({ terminated, ContextID });
    }
}
